import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  Tabs,
  Tab,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
  FilterAlt as FilterAltIcon,
  InfoOutlined as InfoOutlinedIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import BorrowerUpdatesSearchAutocomplete from '../components/BorrowerUpdatesSearchAutocomplete';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { 
  format, 
  formatDistanceToNow, 
  startOfDay, 
  endOfDay, 
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  isWithinInterval, 
  parseISO,
  isBefore
} from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface RawBorrower {
  id: string;
  name: string;
  mobile_no: string;
  address: string;
}

interface RawCollection {
  id: string;
  borrower: RawBorrower | RawBorrower[];
}

interface RawAgent {
  username: string;
  avatar_url?: string;
}

interface RawLoandiskAssignment {
  id: string;
  loandisk_borrower_id: string;
  loandisk_unique_number: string | null;
}

interface RawBorrowerUpdate {
  id: string;
  content: string;
  image_url: string | null;
  created_at: string;
  agent_name: string | null;
  collection: RawCollection | RawCollection[] | null;
  loandisk_assignment: RawLoandiskAssignment | RawLoandiskAssignment[] | null;
  agent: RawAgent | RawAgent[] | null;
}

interface BorrowerUpdate {
  id: string;
  content: string;
  image_url: string | null;
  created_at: string;
  agent_name: string | null;
  collection: {
    id: string;
    borrower: {
      id: string;
      name: string;
      mobile_no: string;
      address: string;
    };
  };
  source: 'legacy' | 'loandisk';
  loandisk_unique_number: string | null;
  agent: {
    username: string;
    avatar_url?: string;
  } | null;
}

// Add styled component for bold table headers
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  whiteSpace: 'nowrap'
}));

// Add new type for date filter options
type DateFilterOption = 
  | 'all' 
  | 'today' 
  | 'yesterday' 
  | 'thisWeek' 
  | 'lastWeek' 
  | 'thisMonth' 
  | 'lastMonth' 
  | '90days' 
  | '120days' 
  | 'custom';

// Add new type for no updates filter options  
type NoUpdatesOption = 'all' | '30days' | '60days' | '90days' | '120days' | 'allTime';

// Add styled component for filter section
const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '0.75rem',
  minWidth: 'auto',
  padding: '6px 8px',
  [theme.breakpoints.up('md')]: {
    padding: '6px 16px',
    fontSize: '0.875rem',
  },
}));

const BorrowerUpdates: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { userProfile } = useAuth();
  const [updates, setUpdates] = useState<BorrowerUpdate[]>([]);
  const [allUpdates, setAllUpdates] = useState<BorrowerUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedBorrowerId, setSelectedBorrowerId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [agents, setAgents] = useState<{id: string, username: string}[]>([]);
  const [isFiltering, setIsFiltering] = useState(false);
  
  // New state for filters
  const [dateFilter, setDateFilter] = useState<DateFilterOption>('all');
  const [noUpdatesFilter, setNoUpdatesFilter] = useState<NoUpdatesOption>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [activeFilterTab, setActiveFilterTab] = useState(0);
  const [uniqueBorrowers, setUniqueBorrowers] = useState<Map<string, Date>>(new Map());
  
  const fetchBorrowerUpdates = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Starting to fetch borrower updates...');

      // Fetch borrower updates that are not deleted
      const { data, error } = await supabase
        .from('borrower_updates')
        .select(`
          id,
          content,
          image_url,
          created_at,
          agent_name,
          loandisk_collection_assignment_id,
          collection:for_collection!collection_id (
            id,
            borrower:borrowers!inner (
              id,
              name,
              mobile_no,
              address
            )
          ),
          loandisk_assignment:loandisk_collection_assignments!loandisk_collection_assignment_id (
            id,
            loandisk_borrower_id,
            loandisk_unique_number
          ),
          agent:profiles!agent_id (
            username,
            avatar_url
          )
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching borrower updates:', error);
        throw error;
      }

      const assignmentBorrowerIds = Array.from(new Set((data || []).flatMap((item: any) => {
        const assignment = Array.isArray(item.loandisk_assignment)
          ? item.loandisk_assignment[0]
          : item.loandisk_assignment;
        return assignment?.loandisk_borrower_id ? [assignment.loandisk_borrower_id] : [];
      })));
      const loandiskBorrowers = new Map<
        string,
        { id: string; name: string; mobile_no: string; address: string }
      >();
      for (let offset = 0; offset < assignmentBorrowerIds.length; offset += 500) {
        const { data: mirrorBorrowers, error: mirrorError } = await supabase
          .from('loandisk_borrowers')
          .select('loandisk_borrower_id, full_name, first_name, last_name, mobile, address')
          .in('loandisk_borrower_id', assignmentBorrowerIds.slice(offset, offset + 500));
        if (mirrorError) throw mirrorError;
        (mirrorBorrowers || []).forEach((borrower: any) => {
          loandiskBorrowers.set(borrower.loandisk_borrower_id, {
            id: borrower.loandisk_borrower_id,
            name: borrower.full_name
              || [borrower.first_name, borrower.last_name].filter(Boolean).join(' ')
              || 'Unnamed borrower',
            mobile_no: borrower.mobile || '',
            address: borrower.address || '',
          });
        });
      }

      // Transform both legacy collection updates and LoanDisk assignment updates.
      const transformedData: BorrowerUpdate[] = (data || []).map((item: any) => {
        const rawUpdate = item as RawBorrowerUpdate;
        let borrowerInfo: RawBorrower = {
          id: '',
          name: 'Unknown',
          mobile_no: '',
          address: ''
        };

        let collectionId = '';
        let source: 'legacy' | 'loandisk' = 'legacy';
        let loandiskUniqueNumber: string | null = null;

        // Extract collection data (could be array or object)
        if (Array.isArray(rawUpdate.collection) && rawUpdate.collection.length > 0) {
          const collection = rawUpdate.collection[0];
          collectionId = collection.id || '';
          
          // Handle borrower data (could be array or object)
          if (collection.borrower) {
            if (Array.isArray(collection.borrower) && collection.borrower.length > 0) {
              const b = collection.borrower[0];
              borrowerInfo = {
                id: b.id || '',
                name: b.name || 'Unknown',
                mobile_no: b.mobile_no || '',
                address: b.address || ''
              };
            } else {
              const b = collection.borrower as RawBorrower;
              borrowerInfo = {
                id: b.id || '',
                name: b.name || 'Unknown',
                mobile_no: b.mobile_no || '',
                address: b.address || ''
              };
            }
          }
        } else if (rawUpdate.collection) {
          const collection = rawUpdate.collection as RawCollection;
          collectionId = collection.id || '';
          
          if (collection.borrower) {
            if (Array.isArray(collection.borrower) && collection.borrower.length > 0) {
              const b = collection.borrower[0];
              borrowerInfo = {
                id: b.id || '',
                name: b.name || 'Unknown',
                mobile_no: b.mobile_no || '',
                address: b.address || ''
              };
            } else {
              const b = collection.borrower as RawBorrower;
              borrowerInfo = {
                id: b.id || '',
                name: b.name || 'Unknown',
                mobile_no: b.mobile_no || '',
                address: b.address || ''
              };
            }
          }
        }

        const rawAssignment = Array.isArray(rawUpdate.loandisk_assignment)
          ? rawUpdate.loandisk_assignment[0]
          : rawUpdate.loandisk_assignment;
        if (!collectionId && rawAssignment) {
          source = 'loandisk';
          collectionId = rawAssignment.id || '';
          loandiskUniqueNumber = rawAssignment.loandisk_unique_number || null;
          borrowerInfo = loandiskBorrowers.get(rawAssignment.loandisk_borrower_id) || borrowerInfo;
        }

        // Extract agent data (could be array or object)
        let agentInfo = null;
        if (Array.isArray(rawUpdate.agent) && rawUpdate.agent.length > 0) {
          agentInfo = {
            username: rawUpdate.agent[0].username || '',
            avatar_url: rawUpdate.agent[0].avatar_url
          };
        } else if (rawUpdate.agent) {
          const agent = rawUpdate.agent as RawAgent;
          agentInfo = {
            username: agent.username || '',
            avatar_url: agent.avatar_url
          };
        }

        // Create a properly formatted update object
        return {
          id: rawUpdate.id || '',
          content: rawUpdate.content || '',
          image_url: rawUpdate.image_url,
          created_at: rawUpdate.created_at || new Date().toISOString(),
          agent_name: rawUpdate.agent_name || '',
          collection: {
            id: collectionId,
            borrower: borrowerInfo
          },
          source,
          loandisk_unique_number: loandiskUniqueNumber,
          agent: agentInfo
        };
      });

      // Store all updates and build unique borrowers map
      setAllUpdates(transformedData);
      
      // Build a map of borrowers with their latest update date
      const borrowersMap = new Map<string, Date>();
      transformedData.forEach(update => {
        const borrowerId = update.collection?.borrower?.id;
        if (borrowerId) {
          const updateDate = new Date(update.created_at);
          const existingDate = borrowersMap.get(borrowerId);
          
          // If no existing date or this update is more recent
          if (!existingDate || updateDate > existingDate) {
            borrowersMap.set(borrowerId, updateDate);
          }
        }
      });
      
      setUniqueBorrowers(borrowersMap);
      setUpdates(transformedData);
      setLoading(false);
      
    } catch (err) {
      console.error('Error in fetchBorrowerUpdates:', err);
      setLoading(false);
    }
  }, []);

  const fetchAgents = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username')
        .in('role', ['agent', 'superagent'])
        .order('username');

      if (error) throw error;
      setAgents(data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  useEffect(() => {
    fetchBorrowerUpdates();
    fetchAgents();
  }, [fetchBorrowerUpdates]);

  // Monitor filter changes - apply filters to updates
  useEffect(() => {
    applyFilters();
  }, [allUpdates, selectedBorrowerId, selectedAgent, dateFilter, noUpdatesFilter, startDate, endDate]);

  // Apply all filters
  const applyFilters = () => {
    setIsFiltering(true);
    setPage(0);
    
    try {
      let filtered = [...allUpdates];
      
      // Apply borrower filter - selectedBorrowerId is actually the collection ID
      if (selectedBorrowerId) {
        filtered = filtered.filter(update => 
          update.collection?.id === selectedBorrowerId
        );
      }
      
      // Apply agent filter
      if (selectedAgent) {
        filtered = filtered.filter(update => 
          update.agent?.username === selectedAgent || update.agent_name === selectedAgent
        );
      }
      
      // If "no updates" filter is active, we need to handle borrowers differently
      if (noUpdatesFilter !== 'all') {
        // Get all unique borrowers and their most recent update date
        const noUpdatesThreshold = getNoUpdatesThresholdDate(noUpdatesFilter);
        
        if (noUpdatesThreshold) {
          // Find borrowers with no updates since the threshold
          const borrowersWithNoRecentUpdates = Array.from(uniqueBorrowers.entries())
            .filter(([_, lastUpdateDate]) => isBefore(lastUpdateDate, noUpdatesThreshold))
            .map(([borrowerId]) => borrowerId);
          
          // Only keep updates from these borrowers
          filtered = filtered.filter(update => 
            borrowersWithNoRecentUpdates.includes(update.collection?.borrower?.id || '')
          );
        }
      } 
      // Otherwise apply date range filter
      else if (dateFilter !== 'all') {
        const { start, end } = getDateRange(dateFilter);
        
        if (start && end) {
          filtered = filtered.filter(update => {
            const updateDate = new Date(update.created_at);
            return isWithinInterval(updateDate, { start, end });
          });
        }
      }
      
      setUpdates(filtered);
    } catch (err) {
      console.error('Error applying filters:', err);
    } finally {
      setIsFiltering(false);
    }
  };

  // Get date range based on the selected filter
  const getDateRange = (filter: DateFilterOption): { start: Date | null; end: Date | null } => {
    const now = new Date();
    
    switch (filter) {
      case 'today':
        return { 
          start: startOfDay(now), 
          end: endOfDay(now) 
        };
        
      case 'yesterday': {
        const yesterday = subDays(now, 1);
        return { 
          start: startOfDay(yesterday), 
          end: endOfDay(yesterday) 
        };
      }
        
      case 'thisWeek':
        return { 
          start: startOfWeek(now, { weekStartsOn: 1 }), 
          end: endOfWeek(now, { weekStartsOn: 1 }) 
        };
        
      case 'lastWeek': {
        const lastWeekStart = subDays(startOfWeek(now, { weekStartsOn: 1 }), 7);
        const lastWeekEnd = subDays(endOfWeek(now, { weekStartsOn: 1 }), 7);
        return { 
          start: lastWeekStart, 
          end: lastWeekEnd 
        };
      }
        
      case 'thisMonth':
        return { 
          start: startOfMonth(now), 
          end: endOfMonth(now) 
        };
        
      case 'lastMonth': {
        const lastMonth = subMonths(now, 1);
        return { 
          start: startOfMonth(lastMonth), 
          end: endOfMonth(lastMonth) 
        };
      }
        
      case '90days':
        return { 
          start: subDays(now, 90), 
          end: now 
        };
        
      case '120days':
        return { 
          start: subDays(now, 120), 
          end: now 
        };
        
      case 'custom':
        return { 
          start: startDate ? startOfDay(startDate) : null, 
          end: endDate ? endOfDay(endDate) : null 
        };
        
      default:
        return { start: null, end: null };
    }
  };

  // Get threshold date for "no updates since" filters
  const getNoUpdatesThresholdDate = (filter: NoUpdatesOption): Date | null => {
    const now = new Date();
    
    switch (filter) {
      case '30days':
        return subDays(now, 30);
      case '60days':
        return subDays(now, 60);
      case '90days':
        return subDays(now, 90);
      case '120days':
        return subDays(now, 120);
      case 'allTime':
        // Return a very old date to match everything
        return new Date(2000, 0, 1);
      default:
        return null;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBorrowerSelect = (collectionId: string | null) => {
    setSelectedBorrowerId(collectionId);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchBorrowerUpdates();
    setRefreshing(false);
  };

  const clearAllFilters = () => {
    setSelectedBorrowerId(null);
    setSelectedAgent('');
    setDateFilter('all');
    setNoUpdatesFilter('all');
    setStartDate(null);
    setEndDate(null);
    setActiveFilterTab(0);
  };

  const getAgentChipStyle = (agentName: string) => {
    // Direct mapping of agents to specific colors
    const agentColors: { [key: string]: string } = {
      'Cleto': '#FFB74D',      // Orange
      'Fernan': '#4FC3F7',     // Blue
      'Fredo': '#81C784',      // Green
      'June Sheen': '#FF8A65', // Coral
      'Kelvin': '#9575CD',     // Purple
      'Noel': '#4DB6AC',       // Teal
      'SammieCRM': '#F06292',  // Pink
      // Add fallback colors for any new agents
      'default1': '#FFD54F',   // Amber
      'default2': '#7986CB',   // Indigo
      'default3': '#A1887F'    // Brown
    };

    return {
      backgroundColor: agentColors[agentName] || agentColors.default1,
      color: '#000000',
      borderRadius: '16px',
      padding: '4px 12px',
      fontSize: '0.875rem',
      fontWeight: 500,
      border: '1px solid rgba(0, 0, 0, 0.12)',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      minWidth: '80px',
      textAlign: 'center',
      opacity: 0.8 // Making colors slightly softer for better readability
    };
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return {
        formattedDate: format(date, 'MMM dd, yyyy'),
        formattedTime: format(date, 'h:mm a'),
        relativeTime: formatDistanceToNow(date, { addSuffix: true })
      };
    } catch (e) {
      return {
        formattedDate: 'Invalid date',
        formattedTime: '',
        relativeTime: ''
      };
    }
  };

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'all': return 'All Updates';
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'thisWeek': return 'This Week';
      case 'lastWeek': return 'Last Week';
      case 'thisMonth': return 'This Month';
      case 'lastMonth': return 'Last Month';
      case '90days': return 'Last 90 Days';
      case '120days': return 'Last 120 Days';
      case 'custom': return 'Custom Range';
      default: return 'All Updates';
    }
  };

  const getNoUpdatesFilterLabel = () => {
    switch (noUpdatesFilter) {
      case 'all': return 'All Borrowers';
      case '30days': return 'No Updates: 30+ Days';
      case '60days': return 'No Updates: 60+ Days';
      case '90days': return 'No Updates: 90+ Days';
      case '120days': return 'No Updates: 120+ Days';
      case 'allTime': return 'Never Updated';
      default: return 'All Borrowers';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        py: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 1.5, sm: 3 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 0, sm: 3 },
            border: { xs: 'none', sm: '1px solid' },
            borderColor: 'divider',
            borderRadius: { xs: 0, sm: 2 },
            bgcolor: { xs: 'transparent', sm: 'background.paper' },
            position: 'relative'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Borrower Profile Recent Updates
            </Typography>
            
            <IconButton 
              color="primary" 
              onClick={handleRefresh} 
              disabled={refreshing}
              sx={{ ml: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Filter Tabs */}
          <Tabs 
            value={activeFilterTab}
            onChange={(_, newValue) => setActiveFilterTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <StyledTab label="Filters" value={0} />
            <StyledTab label="Date Filters" value={1} />
            <StyledTab label="No Updates Filters" value={2} />
          </Tabs>

          {/* Main filters */}
          {activeFilterTab === 0 && (
            <FilterSection>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <BorrowerUpdatesSearchAutocomplete
                    onBorrowerSelect={handleBorrowerSelect}
                  />
                </Grid>
                
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth>
                    <InputLabel id="agent-filter-label">Filter by Agent</InputLabel>
                    <Select
                      labelId="agent-filter-label"
                      value={selectedAgent}
                      label="Filter by Agent"
                      onChange={(e) => setSelectedAgent(e.target.value)}
                      sx={{
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>All Agents</em>
                      </MenuItem>
                      {agents.map((agent) => (
                        <MenuItem 
                          key={agent.id} 
                          value={agent.username}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Typography 
                            component="span" 
                            sx={getAgentChipStyle(agent.username)}
                          >
                            {agent.username}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Button 
                    fullWidth 
                    variant="outlined"
                    color="primary"
                    sx={{ height: '100%' }}
                    onClick={clearAllFilters}
                    startIcon={<ClearIcon />}
                  >
                    Clear All
                  </Button>
                </Grid>
              </Grid>
            </FilterSection>
          )}

          {/* Date filter options */}
          {activeFilterTab === 1 && (
            <FilterSection>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Filter by Update Date
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="date-filter-label">Date Range</InputLabel>
                    <Select
                      labelId="date-filter-label"
                      value={dateFilter}
                      label="Date Range"
                      onChange={(e) => {
                        setDateFilter(e.target.value as DateFilterOption);
                        setNoUpdatesFilter('all'); // Reset the other filter
                      }}
                    >
                      <MenuItem value="all">All Updates</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="yesterday">Yesterday</MenuItem>
                      <MenuItem value="thisWeek">This Week</MenuItem>
                      <MenuItem value="lastWeek">Last Week</MenuItem>
                      <MenuItem value="thisMonth">This Month</MenuItem>
                      <MenuItem value="lastMonth">Last Month</MenuItem>
                      <MenuItem value="90days">Last 90 Days</MenuItem>
                      <MenuItem value="120days">Last 120 Days</MenuItem>
                      <MenuItem value="custom">Custom Date Range</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {dateFilter === 'custom' && (
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <DatePicker
                          label="Start Date"
                          value={startDate}
                          onChange={(newValue) => setStartDate(newValue)}
                          slotProps={{ textField: { size: 'small' } }}
                        />
                        <DatePicker
                          label="End Date"
                          value={endDate}
                          onChange={(newValue) => setEndDate(newValue)}
                          slotProps={{ textField: { size: 'small' } }}
                        />
                      </Stack>
                    </LocalizationProvider>
                  </Grid>
                )}
              </Grid>
            </FilterSection>
          )}

          {/* No updates filter options */}
          {activeFilterTab === 2 && (
            <FilterSection>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Filter Borrowers with No Updates
                </Typography>
                <Tooltip title="Shows borrowers who haven't had updates in the specified time period">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <FormControl fullWidth size="small">
                <InputLabel id="no-updates-filter-label">No Updates Since</InputLabel>
                <Select
                  labelId="no-updates-filter-label"
                  value={noUpdatesFilter}
                  label="No Updates Since"
                  onChange={(e) => {
                    setNoUpdatesFilter(e.target.value as NoUpdatesOption);
                    setDateFilter('all'); // Reset the other filter
                  }}
                >
                  <MenuItem value="all">Show All Borrowers</MenuItem>
                  <MenuItem value="30days">No Updates for 30+ Days</MenuItem>
                  <MenuItem value="60days">No Updates for 60+ Days</MenuItem>
                  <MenuItem value="90days">No Updates for 90+ Days</MenuItem>
                  <MenuItem value="120days">No Updates for 120+ Days</MenuItem>
                  <MenuItem value="allTime">Never Updated</MenuItem>
                </Select>
              </FormControl>
            </FilterSection>
          )}

          {/* Active filters display */}
          {(selectedBorrowerId || selectedAgent || dateFilter !== 'all' || noUpdatesFilter !== 'all') && (
            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                mb: 2,
                p: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider'
              }}
            >
              <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                Active filters:
              </Typography>
              
              {selectedBorrowerId && (
                <Chip 
                  label={`Borrower: ${updates.find(u => u.collection?.id === selectedBorrowerId)?.collection?.borrower?.name || 'Unknown'}`} 
                  size="small" 
                  onDelete={() => setSelectedBorrowerId(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
              
              {selectedAgent && (
                <Chip 
                  label={`Agent: ${selectedAgent}`} 
                  size="small" 
                  onDelete={() => setSelectedAgent('')}
                  color="primary"
                  variant="outlined"
                />
              )}
              
              {dateFilter !== 'all' && (
                <Chip 
                  label={`Date: ${getDateFilterLabel()}`} 
                  size="small" 
                  onDelete={() => setDateFilter('all')}
                  color="primary"
                  variant="outlined"
                  icon={<DateRangeIcon />}
                />
              )}
              
              {noUpdatesFilter !== 'all' && (
                <Chip 
                  label={getNoUpdatesFilterLabel()} 
                  size="small" 
                  onDelete={() => setNoUpdatesFilter('all')}
                  color="primary"
                  variant="outlined"
                  icon={<FilterAltIcon />}
                />
              )}
              
              <Chip 
                label="Clear All" 
                size="small" 
                onClick={clearAllFilters}
                color="secondary"
                variant="outlined"
              />
            </Box>
          )}

          {isFiltering || refreshing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : updates.length === 0 ? (
            <Box sx={{ 
              py: 6, 
              textAlign: 'center',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No updates found matching your filter criteria
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={clearAllFilters}
                startIcon={<ClearIcon />}
                sx={{ mt: 1 }}
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
            <>
              <Box>
                {(rowsPerPage > 0
                  ? updates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : updates
                ).map((update) => {
                  const { formattedDate, formattedTime, relativeTime } = formatDate(update.created_at);
                  return (
                    <Card 
                      key={update.id} 
                      sx={{ 
                        mb: 2, 
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                      onClick={() => navigate(
                        update.source === 'loandisk' && update.loandisk_unique_number
                          ? `/loandisk-borrower/${encodeURIComponent(update.loandisk_unique_number)}`
                          : `/borrower/${update.collection.id}`
                      )}
                    >
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              mb: 1,
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: { xs: 1, sm: 0 }
                            }}>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  color: 'primary.main' 
                                }}
                              >
                                {update.collection?.borrower?.name || 'Unknown Borrower'}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                <DateRangeIcon fontSize="inherit" />
                                {relativeTime} • {formattedDate} {formattedTime}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Typography 
                                component="span"
                                variant="body2"
                                sx={getAgentChipStyle(update.agent?.username || update.agent_name || 'Unknown')}
                              >
                                {update.agent?.username || update.agent_name || 'Unknown Agent'}
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                my: 1, 
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.6,
                                color: theme.palette.text.primary
                              }}
                            >
                              {update.content}
                            </Typography>
                            {update.image_url && (
                              <Box 
                                sx={{ 
                                  mt: 2, 
                                  display: 'flex', 
                                  justifyContent: 'center',
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  border: '1px solid',
                                  borderColor: 'divider' 
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  sx={{ 
                                    maxHeight: 300, 
                                    width: 'auto', 
                                    maxWidth: '100%',
                                  }}
                                  image={update.image_url}
                                  alt="Update image"
                                />
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min(updates.length, rowsPerPage)} of {updates.length} updates
                </Typography>
                
                <TablePagination
                  component="div"
                  count={updates.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[100, 25, 50, 200]}
                  sx={{ 
                    '.MuiTablePagination-toolbar': {
                      pl: { xs: 1, sm: 2 },
                      pr: { xs: 1, sm: 2 },
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default BorrowerUpdates;
